import {process} from "@progress/kendo-data-query";
import {GridColumn as Column} from "@progress/kendo-react-grid/dist/npm/GridColumn";
import {Grid, GridToolbar} from "@progress/kendo-react-grid";
import {useEffect, useRef, useState} from "react";
import useBankNotificationStore from "../../stores/BankNotificationStore";
import useCashflowStore from "../../stores/CashflowStore";
import Waiting from "../waiting/Waiting";
import {Button} from "@progress/kendo-react-buttons";
import {DropDownList} from "@progress/kendo-react-dropdowns";
import {Chip} from "@progress/kendo-react-buttons";
import MatchDialog from "./MatchDialog";
import EditBankNotificationDialog from "./EditBankNotificationDialog";
import config from "../../config/config.json";

const initialDataState = {
    take: 50,
    skip: 0,
    sort: [
        {dir: "desc", field: "ID"}
    ],
};

const statusColors = {
    pending: {background: "#ffc107", color: "#000"},
    matched: {background: "#28a745", color: "#fff"},
    unmatched: {background: "#dc3545", color: "#fff"},
    ignored: {background: "#6c757d", color: "#fff"},
};

const statusLabels = {
    pending: "Ke zpracování",
    matched: "Spárováno",
    unmatched: "Nespárováno",
    ignored: "Ignorováno",
};

const transactionTypeLabels = {
    incoming: "Příchozí",
    outgoing: "Odchozí",
};

const transactionTypeColors = {
    incoming: {background: "#28a745", color: "#fff"},
    outgoing: {background: "#dc3545", color: "#fff"},
};

const notificationTypeColors = {
    sepa: {background: "#007bff", color: "#fff"},
    domestic: {background: "#6c757d", color: "#fff"},
};

const BankNotificationGrid = (props) => {

    const refSize = props.size
    const menuSize = props.menuSize

    const [dataState, setDataState] = useState(initialDataState);
    const [pageSizeValue, setPageSizeValue] = useState(50);

    const fileInputRef = useRef(null);

    // Store
    const notifications = useBankNotificationStore(state => state.notifications)
    const isLoading = useBankNotificationStore(state => state.isLoading)
    const loadNotifications = useBankNotificationStore(state => state.loadNotifications)
    const uploadPDF = useBankNotificationStore(state => state.uploadPDF)
    const processAll = useBankNotificationStore(state => state.processAll)
    const ignoreNotification = useBankNotificationStore(state => state.ignoreNotification)
    const updateNotification = useBankNotificationStore(state => state.updateNotification)
    const statusFilter = useBankNotificationStore(state => state.statusFilter)
    const setStatusFilter = useBankNotificationStore(state => state.setStatusFilter)

    const loadCashflowAll = useCashflowStore(state => state.loadCashflowAll)

    // Dialog state
    const [showMatchDialog, setShowMatchDialog] = useState(false)
    const [matchItem, setMatchItem] = useState(null)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [editItem, setEditItem] = useState(null)

    useEffect(() => {
        loadNotifications()
        loadCashflowAll()
    }, [])

    // Status cell render
    const StatusCell = (props) => {
        const status = props.dataItem.Status
        const style = statusColors[status] || {}
        return (
            <td>
                <Chip
                    text={statusLabels[status] || status}
                    style={{
                        backgroundColor: style.background,
                        color: style.color
                    }}
                />
            </td>
        )
    }

    // Transaction type cell render
    const TransactionTypeCell = (props) => {
        const type = props.dataItem.TransactionType
        const notifType = props.dataItem.NotificationType
        const style = transactionTypeColors[type] || {}
        const sepaStyle = notificationTypeColors.sepa
        return (
            <td>
                <Chip
                    text={transactionTypeLabels[type] || type}
                    style={{
                        backgroundColor: style.background,
                        color: style.color
                    }}
                />
                {notifType === 'sepa' && (
                    <Chip
                        text="SEPA"
                        style={{
                            backgroundColor: sepaStyle.background,
                            color: sepaStyle.color,
                            marginLeft: 4
                        }}
                    />
                )}
            </td>
        )
    }

    // Amount cell render - green for incoming, red for outgoing (shown as negative)
    const AmountCell = (props) => {
        const amount = props.dataItem.Amount
        const currency = props.dataItem.Currency
        const type = props.dataItem.TransactionType
        const isOutgoing = type === 'outgoing'
        const displayAmount = isOutgoing ? -amount : amount

        const formatter = new Intl.NumberFormat('cs-CZ', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        const color = isOutgoing ? '#dc3545' : '#28a745'

        return (
            <td style={{textAlign: 'right', color: color, fontWeight: 'bold'}}>
                {formatter.format(displayAmount)} {currency}
            </td>
        )
    }

    // Amount footer - sum of all amounts (incoming positive, outgoing negative)
    const AmountFooter = (props) => {
        const total = notifications.reduce((sum, item) => {
            const amount = item.Amount || 0
            return sum + (item.TransactionType === 'outgoing' ? -amount : amount)
        }, 0)

        const formatter = new Intl.NumberFormat('cs-CZ', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        const color = total >= 0 ? '#28a745' : '#dc3545'

        return (
            <td style={{textAlign: 'right', color: color, fontWeight: 'bold'}}>
                {formatter.format(total)} CZK
            </td>
        )
    }

    // Date cell render
    const DateCell = (props) => {
        const date = props.dataItem[props.field]
        if (!date) return <td></td>
        const d = new Date(date)
        return (
            <td>
                {d.toLocaleDateString('cs-CZ')}
            </td>
        )
    }

    // PDF cell render - icon to download/view PDF
    const PdfCell = (props) => {
        const item = props.dataItem
        if (!item.RawPdfPath) return <td></td>

        // Extract relative path from full path (after /files/)
        const getRelativePath = (fullPath) => {
            if (!fullPath) return null
            const marker = '/files/'
            const idx = fullPath.indexOf(marker)
            if (idx >= 0) {
                return fullPath.substring(idx + marker.length)
            }
            // Fallback: try to get just the filename part after bank_notifications/
            const bnMarker = 'bank_notifications/'
            const bnIdx = fullPath.indexOf(bnMarker)
            if (bnIdx >= 0) {
                return fullPath.substring(bnIdx)
            }
            return null
        }

        const relativePath = getRelativePath(item.RawPdfPath)
        if (!relativePath) return <td></td>

        const pdfUrl = config.FILE_URL + relativePath

        return (
            <td style={{textAlign: 'center'}}>
                <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Zobrazit PDF"
                    style={{color: '#d9534f', fontSize: '18px'}}
                >
                    <span className="k-icon k-i-pdf" />
                </a>
            </td>
        )
    }

    // Action cell render
    const ActionCell = (props) => {
        const item = props.dataItem
        const canMatch = item.Status === 'pending' || item.Status === 'unmatched'
        const canIgnore = item.Status === 'pending' || item.Status === 'unmatched'

        return (
            <td>
                <Button
                    size="small"
                    themeColor="base"
                    onClick={() => openEditDialog(item)}
                    style={{marginRight: 4}}
                    title="Editovat"
                >
                    <span className="k-icon k-i-edit" />
                </Button>
                {canMatch && (
                    <Button
                        size="small"
                        themeColor="primary"
                        onClick={() => openMatchDialog(item)}
                        style={{marginRight: 4}}
                    >
                        Spárovat
                    </Button>
                )}
                {canIgnore && (
                    <Button
                        size="small"
                        themeColor="base"
                        onClick={() => handleIgnore(item)}
                    >
                        Ignorovat
                    </Button>
                )}
                {item.Status === 'matched' && item.MatchedCashflowID && (
                    <span style={{marginLeft: 4}}>CF #{item.MatchedCashflowID}</span>
                )}
            </td>
        )
    }

    // Handlers
    const openMatchDialog = (item) => {
        setMatchItem(item)
        setShowMatchDialog(true)
    }

    const closeMatchDialog = () => {
        setShowMatchDialog(false)
        setMatchItem(null)
    }

    const openEditDialog = (item) => {
        setEditItem(item)
        setShowEditDialog(true)
    }

    const closeEditDialog = () => {
        setShowEditDialog(false)
        setEditItem(null)
    }

    const handleSaveEdit = async (data) => {
        try {
            await updateNotification(data)
            closeEditDialog()
        } catch (error) {
            alert('Chyba při ukládání: ' + error.message)
        }
    }

    const handleIgnore = async (item) => {
        if (window.confirm('Opravdu chcete ignorovat tuto notifikaci?')) {
            await ignoreNotification(item.ID)
        }
    }

    const handleUploadClick = () => {
        fileInputRef.current?.click()
    }

    const handleFileChange = async (event) => {
        const files = event.target.files
        if (files && files.length > 0) {
            try {
                await uploadPDF(files)
                alert('PDF úspěšně nahráno a zpracováno')
            } catch (error) {
                alert('Chyba při nahrávání: ' + error.message)
            }
        }
        // Reset input
        event.target.value = ''
    }

    const handleProcessAll = async () => {
        try {
            const result = await processAll()
            alert(`Zpracováno ${result.processed} notifikací`)
        } catch (error) {
            alert('Chyba při zpracování: ' + error.message)
        }
    }

    const statusFilterItems = [
        {text: "Vše", value: null},
        {text: "Ke zpracování", value: "pending"},
        {text: "Spárováno", value: "matched"},
        {text: "Nespárováno", value: "unmatched"},
        {text: "Ignorováno", value: "ignored"},
    ]

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.value.value)
    }

    const pageChange = (event) => {
        const targetEvent = event.syntheticEvent;
        const take = targetEvent.value === "All" ? notifications.length : event.page.take;

        if (targetEvent.value) {
            setPageSizeValue(targetEvent.value);
        }

        setDataState({
            ...dataState,
            skip: event.page.skip,
            take: event.page.take
        });
    };

    const CountFooter = (props) => {
        const total = process(notifications, dataState).total
        return (
            <td colSpan={props.colSpan} style={props.style}>
                {total} záznamů
            </td>
        );
    };

    if (isLoading && notifications.length === 0) {
        return <Waiting />
    }

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                style={{display: 'none'}}
                accept=".pdf"
                multiple
                onChange={handleFileChange}
            />

            <Grid
                sortable={true}
                filterable={true}
                resizable={true}
                reorderable={true}
                size={"small"}

                style={{
                    height: "100%",
                    width: refSize.width - menuSize,
                }}

                data={process(notifications, dataState)}
                {...dataState}

                total={process(notifications, dataState).total}
                pageable={{
                    buttonCount: 4,
                    pageSizes: [10, 50, 100, 500],
                    pageSizeValue: pageSizeValue,
                }}
                onPageChange={pageChange}
                onDataStateChange={(e) => setDataState(e.dataState)}
            >

                <GridToolbar>
                    <Button
                        themeColor="primary"
                        onClick={handleUploadClick}
                        style={{marginRight: 8}}
                    >
                        Nahrát PDF
                    </Button>

                    <Button
                        themeColor="base"
                        onClick={handleProcessAll}
                        style={{marginRight: 16}}
                    >
                        Zpracovat vše
                    </Button>

                    <DropDownList
                        data={statusFilterItems}
                        textField="text"
                        dataItemKey="value"
                        value={statusFilterItems.find(i => i.value === statusFilter) || statusFilterItems[0]}
                        onChange={handleStatusFilterChange}
                        style={{width: 150}}
                    />

                    <Button
                        themeColor="base"
                        onClick={loadNotifications}
                        style={{marginLeft: 8}}
                        icon="refresh"
                    >
                        Obnovit
                    </Button>
                </GridToolbar>

                <Column field="ID" title="ID" width="70px" filterable={false} />

                <Column
                    field="RawPdfPath"
                    title="PDF"
                    width="50px"
                    cell={PdfCell}
                    filterable={false}
                    sortable={false}
                />

                <Column
                    field="Status"
                    title="Status"
                    width="130px"
                    cell={StatusCell}
                    footerCell={CountFooter}
                />

                <Column
                    field="TransactionType"
                    title="Typ"
                    width="150px"
                    cell={TransactionTypeCell}
                />

                <Column
                    field="TransactionDate"
                    title="Datum transakce"
                    width="130px"
                    cell={DateCell}
                    filter="date"
                />

                <Column
                    field="Amount"
                    title="Částka"
                    width="150px"
                    cell={AmountCell}
                    footerCell={AmountFooter}
                    filter="numeric"
                />

                <Column
                    field="VariableSymbol"
                    title="VS"
                    width="120px"
                />

                <Column
                    field="CounterpartyAccount"
                    title="Protiúčet"
                    width="180px"
                />

                <Column
                    field="CounterpartyName"
                    title="Název protiúčtu"
                    width="200px"
                />

                <Column
                    field="Message"
                    title="Zpráva"
                    width="500px"
                />

                <Column
                    field="PaymentReason"
                    title="Důvod platby"
                    width="200px"
                />

                <Column
                    field="SenderBIC"
                    title="BIC"
                    width="120px"
                />

                <Column
                    field="OurAccount"
                    title="Náš účet"
                    width="180px"
                />

                <Column
                    field="action"
                    title="Akce"
                    width="220px"
                    cell={ActionCell}
                    filterable={false}
                    sortable={false}
                />

            </Grid>

            {showMatchDialog && matchItem && (
                <MatchDialog
                    notification={matchItem}
                    onClose={closeMatchDialog}
                />
            )}

            {showEditDialog && editItem && (
                <EditBankNotificationDialog
                    notification={editItem}
                    onClose={closeEditDialog}
                    onSave={handleSaveEdit}
                />
            )}
        </>
    )
}

export default BankNotificationGrid
